import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isAxiosError } from "axios";
import { useUiTranslation } from "@tubo/ui/i18n";
import {
  useGetById,
  usePrecheck,
  useSemanticDataModelCreate,
  useSemanticDataModelUpdate,
  useSubmitPublication,
  getGetByIdQueryKey,
  getListModelVersionsQueryKey,
  getModelReferencesQueryKey,
  getSemanticDataModelListQueryKey,
} from "@tubo/api-models/hooks/semantic-data-model-api.hooks";
import type { ModelValidationIssueVO, UpdateSemanticDataModelReq } from "@tubo/api-models/hooks/semantic-data-model-api.schemas";
import type { ModelDraftContentVO } from "@tubo/api-models/hooks/semantic-data-model-draft-api.schemas";
import { useMemberNameMap } from "@client/components/admin/publications/publication-shared";
import { DesignerToolbar } from "@client/components/semantic-platform/designer/designer-toolbar";
import { DesignerWorkbench } from "@client/components/semantic-platform/designer/designer-workbench";
import { TableModeEditor } from "@client/components/semantic-platform/designer/table-mode-editor";
import { ValidationStrip } from "@client/components/semantic-platform/designer/validation-strip";
import {
  ConflictArchiveDialog,
  LeaveBlockerDialog,
  LeaseWarningBanner,
  PublishRiskConfirmDialog,
  StaleDraftDialog,
  TakenOverBanner,
  TakeoverConfirmDialog,
} from "@client/components/semantic-platform/designer/edit-page-dialogs";
import { buildDefinitionPayload, useModelEditState } from "@client/components/semantic-platform/designer/use-model-edit-state";
import { useModelCollaboration } from "@client/components/semantic-platform/designer/use-model-collaboration";
import { discardModelDraftArchive } from "@client/components/semantic-platform/designer/model-draft-archive-api";
import { NS_CONTEXT_ALL, NS_CONTEXT_GLOBAL } from "@tubo/semantic-concepts";

type DetailsTranslate = ReturnType<typeof useUiTranslation>["t"];

export const Route = createFileRoute("/_app/semantic-platform/data-models/edit/$id")({
  validateSearch: (search: Record<string, unknown>): { ns?: string } => ({
    ...(typeof search.ns === "string" && search.ns ? { ns: search.ns } : {}),
  }),
  component: EditPage,
});

/** 撤销/重做快捷键(焦点在输入框/文本域/可编辑区时不拦截,保留原生文本编辑撤销) */
function useUndoRedoShortcuts(undo: () => void, redo: () => void) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const key = e.key.toLowerCase();
      const isUndo = key === "z" && !e.shiftKey;
      const isRedo = (key === "z" && e.shiftKey) || key === "y";
      if (!isUndo && !isRedo) return;
      const target = e.target;
      if (target instanceof HTMLElement && target.closest("input, textarea, [contenteditable='true']")) return;
      e.preventDefault();
      if (isUndo) undo();
      else redo();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [undo, redo]);
}

/** 接管冲突(6C-1):识别 edit-taken-over 409(他人持有活跃租约) */
function isEditTakenOverError(error: unknown): boolean {
  return isAxiosError<{ key?: string }>(error) && error.response?.data?.key === "semanticDataModel.model.edit-taken-over";
}

/** 提交发布流:校验 → error 阻断 / warning 确认清单 / 直提 */
async function runPublishFlow(input: {
  precheckRefetch: () => Promise<{ data?: ModelValidationIssueVO[] }>;
  submit: () => void;
  setValidation: (issues: ModelValidationIssueVO[]) => void;
  setConfirmIssues: (issues: ModelValidationIssueVO[]) => void;
  t: DetailsTranslate;
}): Promise<void> {
  const res = await input.precheckRefetch();
  const issues = res.data ?? [];
  if (issues.some((i) => i.severity === "error")) {
    input.setValidation(issues);
    toast.error(input.t("modelEditor.publishValidationFailed"));
    return;
  }
  const warnings = issues.filter((i) => i.severity === "warning");
  if (warnings.length > 0) {
    input.setConfirmIssues(warnings);
    return;
  }
  input.submit();
}

/** 冲突草稿恢复流:接管拿回租约 → 归档内容覆盖保存 → 清归档(接管方版本留历史可回溯) */
async function restoreArchiveFlow(input: {
  content: ModelDraftContentVO | undefined;
  restoring: boolean;
  setRestoring: (v: boolean) => void;
  takeover: () => Promise<unknown>;
  update: (data: UpdateSemanticDataModelReq) => Promise<unknown>;
  discard: () => Promise<unknown>;
  dismiss: () => void;
  t: DetailsTranslate;
}): Promise<void> {
  if (!input.content || input.restoring) return;
  input.setRestoring(true);
  try {
    await input.takeover();
    await input.update(input.content);
    // 先等归档删除落库再刷新,避免查询回填已删归档导致弹窗复开
    await input.discard();
    input.dismiss();
    toast.success(input.t("modelEditor.archiveRestored"));
  } catch {
    toast.error(input.t("modelEditor.archiveRestoreFailed"));
  } finally {
    input.setRestoring(false);
  }
}

/**
 * 数据模型设计器(6A-1 沉浸式单页;5.1 页面骨架 + 6.2 场景模型设计器)
 *
 * 顶部固定工具条(校验/试运行/提交发布/保存);画布模式为三栏工作台
 * (左实体目录/中全高画布/右可调宽可编辑检查器);表格模式承载批量维护。
 * 两模式共用同一份编辑状态与保存载荷,切换不丢内容。
 * 发布即快照:模型恒为草稿可编辑,提交发布时对已保存内容打不可变版本快照进审批。
 */
function EditPage() {
  const { t } = useUiTranslation("semanticPlatformDetails");
  const { id } = Route.useParams();
  const { ns } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === "new";
  const { data: model } = useGetById(id, { query: { enabled: !isNew } });
  const defaultNamespaceId = ns && ns !== NS_CONTEXT_ALL && ns !== NS_CONTEXT_GLOBAL ? ns : "";
  // 被接管(WS editTakenOver 且前任=本人):暂停服务端自动暂存 + 顶部横幅提醒
  const [draftUploadPaused, setDraftUploadPaused] = useState(false);
  const [takenOverBy, setTakenOverBy] = useState<string | null>(null);
  const [takenOverVersion, setTakenOverVersion] = useState<string | null>(null);
  const state = useModelEditState({ id, isNew, model, defaultNamespaceId, draftUploadPaused });
  useUndoRedoShortcuts(state.undo, state.redo);
  const memberNameMap = useMemberNameMap();
  const collaboration = useModelCollaboration({
    id,
    isNew,
    enabled: Boolean(model),
    onTakenOver: (d) => {
      setDraftUploadPaused(true);
      setTakenOverBy(d.takenOverBy);
      setTakenOverVersion(d.takenOverVersion);
      toast.warning(t("modelEditor.leaseTakenOver"));
    },
  });
  const memberName = (userId: string | undefined) =>
    (userId ? memberNameMap.get(userId) : undefined) ?? (userId ? t("modelEditor.user", { id: userId.slice(0, 8) }) : t("modelEditor.otherUser"));
  const [validation, setValidation] = useState<ModelValidationIssueVO[] | null>(null);
  // 提交发布风险确认(warning 级问题须显式确认后才真正提交)
  const [publishConfirmIssues, setPublishConfirmIssues] = useState<ModelValidationIssueVO[] | null>(null);
  // 保存前接管确认:他人持活跃租约时,tAdm/sAdm 保存即隐式接管(弹窗确认,普通用户保存会被 409 拦下)
  const [takeoverConfirmOpen, setTakeoverConfirmOpen] = useState(false);
  const [restoringArchive, setRestoringArchive] = useState(false);

  const invalidateModel = () => {
    queryClient.invalidateQueries({ queryKey: getSemanticDataModelListQueryKey() });
    if (!isNew) {
      queryClient.invalidateQueries({ queryKey: getGetByIdQueryKey(id) });
    }
  };

  const createMutation = useSemanticDataModelCreate({
    mutation: {
      onSuccess: (r) => {
        state.setDirty(false);
        queryClient.invalidateQueries({ queryKey: getSemanticDataModelListQueryKey() });
        toast.success(t("modelEditor.created"));
        navigate({ to: "/semantic-platform/data-models/$id", params: { id: r.id }, search: ns ? { ns } : {} });
      },
      onError: () => toast.error(t("modelEditor.createFailed")),
    },
  });

  const updateMutation = useSemanticDataModelUpdate({
    mutation: {
      onSuccess: () => {
        state.setDirty(false);
        invalidateModel();
        toast.success(t("modelEditor.saved"));
        // 保存时轻量校验(不阻断保存):error/warning 进底部校验条,发布闸门仍以显式校验为准
        precheck
          .refetch()
          .then((res) => {
            const issues = (res.data ?? []).filter((i) => i.severity !== "info");
            if (issues.length === 0) return;
            setValidation(res.data ?? []);
            const errors = issues.filter((i) => i.severity === "error").length;
            const warnings = issues.length - errors;
            toast.warning(t("modelEditor.validationSummary", { errors, warnings }));
          })
          .catch(() => undefined);
      },
      onError: (error) => {
        // 接管冲突(6C-1):他人持有活跃租约 → 专属提示并暂停自动暂存
        if (isEditTakenOverError(error)) {
          setDraftUploadPaused(true);
          setTakeoverConfirmOpen(false);
          toast.error(t("modelEditor.editTakenOver"));
          return;
        }
        toast.error(t("modelEditor.saveFailed"));
      },
    },
  });

  // 提交发布(发布即快照):对已保存的工作区内容打版本快照并进入审批
  const submitMutation = useSubmitPublication({
    mutation: {
      onSuccess: (res) => {
        setPublishConfirmIssues(null);
        state.setDirty(false);
        invalidateModel();
        queryClient.invalidateQueries({ queryKey: getListModelVersionsQueryKey(id) });
        queryClient.invalidateQueries({ queryKey: getModelReferencesQueryKey(id) });
        toast.success(
          res.publicationStatus === "approved"
            ? t("modelDetail.published", { id: res.publicationId })
            : t("modelDetail.submitted", { id: res.publicationId }),
        );
      },
      onError: () => toast.error(t("modelEditor.submitFailed")),
    },
  });

  // 6A-5 校验:precheck 三级问题进底部条,点击定位到画布对象
  const precheck = usePrecheck(id, { query: { enabled: false } });
  const handleValidate = () => {
    precheck
      .refetch()
      .then((res) => setValidation(res.data ?? []))
      .catch(() => toast.error(t("modelEditor.validateFailed")));
  };

  const performSave = () => {
    updateMutation.mutate({ id, data: buildDefinitionPayload(state) });
  };

  const handleSave = () => {
    const definition = buildDefinitionPayload(state);

    if (isNew) {
      createMutation.mutate({ data: { ...definition, code: state.basic.code } });
      return;
    }
    // 他人持活跃租约且本人 tAdm/sAdm 才弹接管确认;普通用户直存,由 409 专属提示接住
    if (collaboration.leaseByOther && collaboration.isAdmin) {
      setTakeoverConfirmOpen(true);
      return;
    }
    updateMutation.mutate({ id, data: definition });
  };

  /** 冲突草稿恢复:接管拿回租约 + 归档内容覆盖保存(流见 restoreArchiveFlow) */
  const handleRestoreArchive = () => {
    void restoreArchiveFlow({
      content: collaboration.myArchive?.content,
      restoring: restoringArchive,
      setRestoring: setRestoringArchive,
      takeover: collaboration.takeoverAsync,
      update: (data) => updateMutation.mutateAsync({ id, data }),
      discard: () => discardModelDraftArchive(id).catch(() => undefined),
      dismiss: collaboration.dismissArchive,
      t,
    });
  };

  const handleDiscardArchive = () => {
    // 先等归档删除落库再刷新,避免查询回填已删归档导致弹窗复开
    void discardModelDraftArchive(id).catch(() => undefined).finally(() => collaboration.dismissArchive());
  };

  // 提交发布入口:必须先保存(快照取自已保存内容);error 问题阻断,warning 弹确认清单
  const handlePublish = () => {
    if (isNew) return;
    if (state.isDirty()) {
      toast.info(t("modelEditor.unsavedBeforePublish"));
      return;
    }
    void runPublishFlow({
      precheckRefetch: () => precheck.refetch(),
      submit: () => submitMutation.mutate({ id, data: {} }),
      setValidation,
      setConfirmIssues: setPublishConfirmIssues,
      t,
    }).catch(() => toast.error(t("modelEditor.validateBeforePublishFailed")));
  };

  return (
    <div className="flex h-full flex-col">
      <DesignerToolbar
        isNew={isNew}
        modelId={isNew ? undefined : id}
        name={state.basic.name}
        currentVersion={model?.currentVersion}
        viewMode={state.viewMode}
        onViewModeChange={state.setViewMode}
        canUndo={state.canUndo}
        canRedo={state.canRedo}
        onUndo={state.undo}
        onRedo={state.redo}
        saveVisible
        saving={createMutation.isPending || updateMutation.isPending}
        autoSavedAt={state.lastAutoSavedAt}
        validating={precheck.isFetching}
        // 编辑既有模型:返回=退出编辑回详情页;新建:返回列表(未保存拦截由 blocker 统一兜底)
        onBack={() =>
          isNew
            ? navigate({ to: "/semantic-platform/data-models", search: ns ? { ns } : {} })
            : navigate({ to: "/semantic-platform/data-models/$id", params: { id }, search: ns ? { ns } : {} })
        }
        onSave={handleSave}
        onValidate={handleValidate}
        trialRunVisible={false}
        onTrialRun={() => undefined}
        publishVisible={!isNew && Boolean(model)}
        publishPending={submitMutation.isPending}
        onPublish={handlePublish}
      />

      {/* 接管提醒横幅:他人活跃租约(保存前确认) / 本人被接管(暂停自动暂存,草稿已归档) */}
      {collaboration.leaseByOther && (
        <LeaseWarningBanner
          holderName={memberName(collaboration.leaseHolder)}
          expiresAt={collaboration.leaseExpiresAt}
        />
      )}
      {takenOverBy && (
        <TakenOverBanner byName={memberName(takenOverBy)} version={takenOverVersion} onDismiss={() => setTakenOverBy(null)} />
      )}

      <div className="flex min-h-0 flex-1 flex-col">
        {state.viewMode === "canvas" ? (
          <DesignerWorkbench state={state} modelId={isNew ? undefined : id} conflictCodes={[]} />
        ) : (
          <TableModeEditor state={state} modelId={isNew ? undefined : id} conflictCodes={[]} />
        )}

        {validation && (
          <ValidationStrip
            issues={validation}
            entities={state.entities}
            relationships={state.relationships}
            onLocate={(sel) => {
              state.setViewMode("canvas");
              state.setSelection(sel);
            }}
            onClose={() => setValidation(null)}
          />
        )}
      </div>


      {/* 提交发布风险确认:warning 级问题须显式确认(错误级已在提交前阻断) */}
      {publishConfirmIssues !== null && (
        <PublishRiskConfirmDialog
          issues={publishConfirmIssues}
          onCancel={() => setPublishConfirmIssues(null)}
          onConfirm={() => submitMutation.mutate({ id, data: { confirmedRiskCodes: (publishConfirmIssues ?? []).map((i) => i.code) } })}
        />
      )}

      {/* 陈旧草稿确认:草稿写入后模型已被保存过,由用户决定恢复还是丢弃,不静默覆盖 */}
      {state.staleDraft !== null && (
        <StaleDraftDialog savedAt={state.staleDraft.savedAt} onRestore={state.restoreStaleDraft} onDiscard={state.discardStaleDraft} />
      )}

      {/* 保存前接管确认:他人持活跃租约,tAdm/sAdm 保存即隐式接管(其草稿转冲突归档供恢复) */}
      <TakeoverConfirmDialog
        open={takeoverConfirmOpen}
        holderName={collaboration.leaseHolder ? memberName(collaboration.leaseHolder) : undefined}
        onCancel={() => setTakeoverConfirmOpen(false)}
        onConfirm={() => {
          setTakeoverConfirmOpen(false);
          performSave();
        }}
      />

      {/* 冲突草稿恢复(带摘要对比):你的未保存草稿被接管方归档,恢复将以归档内容覆盖保存(接管方版本留历史) */}
      {collaboration.myArchive && (
        <ConflictArchiveDialog
          archive={collaboration.myArchive}
          takerName={memberName(collaboration.myArchive.takenOverById)}
          restoring={restoringArchive}
          onRestore={handleRestoreArchive}
          onDiscard={handleDiscardArchive}
        />
      )}

      {/* FE-03 安全离开:路由拦截由 AlertDialog 确认(替代浏览器原生 confirm);beforeunload 仍走浏览器提示 */}
      <LeaveBlockerDialog
        open={state.blocker.status === "blocked"}
        onReset={state.blocker.reset}
        onProceed={() => {
          // 显式放弃=草稿一并丢弃(本地+服务端),否则下次进入编辑会被恢复
          state.discardDraft();
          state.blocker.proceed?.();
        }}
      />
    </div>
  );
}
